import {
  ScrollArea as MantineScrollArea,
  ScrollAreaProps,
  Box,
} from "@mantine/core"
import {
  IconChevronDown,
  IconChevronUp,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react"
import _ from "lodash"
import { forwardRef, PropsWithChildren, useRef } from "react"

import { useDragScroll } from "../../hooks/useDragScroll"
import {
  ScrollDirection,
  ScrollDirectionType,
  useScroll,
} from "../../hooks/useScroll"
import { ZoomContainer } from "../ZoomContainer/ZoomContainer"

import classes from "./ScrollArea.module.css"

type EnhancedScrollAreaProps = ScrollAreaProps &
  PropsWithChildren & {
    showEdgeFade?: boolean
    zoomable?: boolean
    hideScrollbar?: boolean
    dragScroll?: boolean
  }

export const ScrollArea = forwardRef<HTMLDivElement, EnhancedScrollAreaProps>(
  (
    {
      children,
      showEdgeFade = false,
      zoomable = false,
      hideScrollbar = false,
      dragScroll = false,
      style,
      styles,
      ...props
    },
    ref
  ) => {
    const scrollViewportRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const { scrollDirection } = useScroll(scrollViewportRef)

    useDragScroll(scrollViewportRef, dragScroll)

    const handleScrollToEdge = (direction: ScrollDirectionType) => {
      if (!scrollViewportRef.current) return

      const options: ScrollToOptions = { behavior: "smooth" }

      switch (direction) {
        case ScrollDirection.LEFT:
          options.left = 0
          break
        case ScrollDirection.RIGHT:
          options.left = scrollViewportRef.current.scrollWidth
          break
        case ScrollDirection.TOP:
          options.top = 0
          break
        case ScrollDirection.BOTTOM:
          options.top = scrollViewportRef.current.scrollHeight
          break
      }

      scrollViewportRef.current.scrollTo(options)
    }

    return (
      <Box pos="relative" ref={containerRef}>
        <MantineScrollArea
          {...props}
          viewportRef={scrollViewportRef}
          ref={ref}
          style={{
            ...(dragScroll ? { cursor: "grab" } : {}),
            ...style,
          }}
          styles={{
            ...(hideScrollbar
              ? {
                  scrollbar: {
                    display: "none",
                  },
                }
              : {}),
            ...styles,
          }}
        >
          {zoomable ? (
            <ZoomContainer>{children}</ZoomContainer>
          ) : (
            <>{children}</>
          )}
        </MantineScrollArea>

        {showEdgeFade &&
          scrollDirection.map((direction) => (
            <ScrollFadeWithArrow
              key={direction}
              direction={direction}
              onClick={() => handleScrollToEdge(direction)}
              icon={ScrollIcons[direction]}
            />
          ))}
      </Box>
    )
  }
)

export const ScrollIcons: Record<ScrollDirectionType, React.ReactNode> = {
  left: <IconChevronLeft size={20} />,
  right: <IconChevronRight size={20} />,
  top: <IconChevronUp size={20} />,
  bottom: <IconChevronDown size={20} />,
}

interface ScrollFadeWithArrowProps {
  direction: ScrollDirectionType
  onClick: () => void
  icon: React.ReactNode
}

const ScrollFadeWithArrow = ({
  direction,
  onClick,
}: ScrollFadeWithArrowProps) => {
  const directionCapitalized = _.capitalize(direction)
  const className = classes[`fade${directionCapitalized}`]
  return (
    <Box className={className}>
      <div
        onClick={onClick}
        className={`${classes.scrollArrow} ${classes[`scrollArrow${directionCapitalized}`]}`}
      >
        {ScrollIcons[direction]}
      </div>
    </Box>
  )
}

ScrollArea.displayName = "ScrollArea"
